import { uniq } from 'lodash';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { getPriceByPricingModel, useFeesBySolutionContext } from 'sonata-platform-components';
import { Tag } from 'sonata-react-components';
import { normalizeWithUnit } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';
import { FeeSolution } from 'sonata-static-storage/lib/schemas/feeSolution/feeSolution.schema';
import { FeeSolutionPlan } from 'sonata-static-storage/lib/schemas/feeSolution/feeSolutionPlan.schema';
import { UserFeeSolutionPlan } from 'sonata-static-storage/lib/schemas/feeSolution/userFeeSolutionPlan.schema';

import { ContextualDataVariables } from '../../ContextualDataVariables/ContextualDataVariables';
import { ProvidersEnum } from '../StepFour/CampaignAdditionalDetails/CampaignAdditionalDetails';
import Card from './Card/Card';
import { SolutionCard } from './SolutionCard/SolutionCard';

import './stepTwo.scss';

type State = {
  selectedSolution?: FeeSolution;
};

export function StepTwo() {
  const { formatMessage } = useIntl();

  const {
    campaign,
    capabilities,
    capabilityCategories,
    contextualThirdPartyProviderVariables,
    currencyInfo,
    getStep2,
    handleContextualDataProviderChange,
    handleStep2Change,
    initialCampaign,
    mode,
    plans,
    solutions,
    user,
    userPlans,
    userRoles
  } = useFeesBySolutionContext();

  const { campaignPlanId } = getStep2();

  const selectedPlan: FeeSolutionPlan | undefined = plans!.find((p: FeeSolutionPlan) => p._id === campaignPlanId)!;
  const selectedUserPlan: UserFeeSolutionPlan | undefined = userPlans!.find(
    (up: UserFeeSolutionPlan) => up.plan === campaignPlanId
  );
  const filledPlansByUser = userPlans!.map((pbu: UserFeeSolutionPlan) => ({
    ...pbu,
    plan: plans.find((p: FeeSolutionPlan) => p._id === pbu.plan)!
  }));

  const [{ selectedSolution }, setState] = useState<State>({
    selectedSolution: solutions!.find((s: Record<string, any>) => s._id === selectedPlan?.feeSolution) || solutions?.[0]
  });

  const isCustom = filledPlansByUser
    .filter((userPlan: Record<string, any>) => userPlan.plan.feeSolution === selectedSolution!._id)
    .some((item: Record<string, any>) => item.plan._id === selectedPlan?._id && item.plan.code === 'custom');

  const handlePlanChange = (planId: string) => {
    handleStep2Change({
      selectedPlan: planId
    });
    if (!isCustom) {
      handleContextualDataProviderChange(ProvidersEnum.mastercard, false, 0, []);
      handleContextualDataProviderChange(ProvidersEnum.circana, false, 0, []);
      handleContextualDataProviderChange(ProvidersEnum.wardem, false, 0, []);
    }
  };

  const handleSolutionChange = (solutionCode: string[]) => {
    const newSelectedSolution = solutions!.find((s: FeeSolution) => s.code === solutionCode[0])!;
    const newSelectedPlan = plans.find((p: FeeSolutionPlan) => p.feeSolution === newSelectedSolution._id);
    setState(prevState => ({
      ...prevState,
      selectedSolution: newSelectedSolution
    }));
    handleStep2Change({
      selectedPlan: newSelectedPlan?._id
    });
  };

  const handleSolutionCardChange = (solutionId: string) => {
    const solution = solutions!.find((s: FeeSolution) => s._id === solutionId);
    if (solution) {
      handleSolutionChange([solution.code]);
    }
  };

  const currentCapabilities =
    capabilities!.filter((c: Record<string, any>) => selectedPlan?.capabilities.includes(c?._id || '')) || [];
  const currentCapabilitiesCategoriesIds = uniq(currentCapabilities.map((c: Record<string, any>) => c.category)) || [];

  if (!solutions || !plans || !userPlans || !capabilities) {
    return <div> Looks like you do not have Fees by Solutions in your user</div>;
  }

  const isCardDisabled = (planCode: string): boolean => {
    if (mode === 'edit' && campaign.completed) {
      const planSelected = plans.find((item: FeeSolutionPlan) => item._id === initialCampaign.solutionsFees.plan);
      if (planSelected) {
        const allPlans = ['standard', 'advanced', 'custom'];
        const planIndex = allPlans.indexOf(planCode);
        const planSelectedIndex = allPlans.indexOf(planSelected.code);
        return planIndex < planSelectedIndex;
      }
    }
    return false;
  };

  return (
    <div className="feesBySolutionStepTwo">
      <div className="feesBySolutionStepTwo__solutionSectionTitle">
        {formatMessage({
          defaultMessage: 'Select Solution',
          id: 'feesBySolution.stepTwo.solutionSectionTitle'
        })}
      </div>
      <div className="feesBySolutionStepTwo__solutionCards">
        {solutions.map((solution: FeeSolution) => (
          <SolutionCard
            active={selectedSolution?._id === solution._id}
            description={solution.description || ''}
            id={solution._id!}
            isDisabled={mode === 'edit'}
            key={`feesBySolutionStepTwo-solutionCard-${solution.code}`}
            onChange={handleSolutionCardChange}
            title={solution.copy}
          />
        ))}
      </div>
      <div className="feesBySolutionStepTwo__planSection">
        <div className="feesBySolutionStepTwo__planSectionTitle">
          {formatMessage({
            defaultMessage: 'Select Planning type',
            id: 'feesBySolution.stepTwo.planSectionTitle'
          })}
        </div>
        <div className="feesBySolutionStepTwo__planSectionSelection">
          {filledPlansByUser
            .filter((userPlan: Record<string, any>) => userPlan.plan.feeSolution === selectedSolution!._id)
            .map(({ plan, price, pricingModel }: Record<string, any>) => {
              const selectedPricingModel = pricingModel || selectedSolution?.pricingModel;
              return (
                <Card
                  active={selectedPlan?._id === plan._id}
                  id={plan._id!}
                  isDisabled={isCardDisabled(plan.code)}
                  key={`feesBySolutionStepTwo-planSectionSelectionCard-${plan.code}`}
                  onChange={handlePlanChange}
                  price={`+${normalizeWithUnit(getPriceByPricingModel(price, selectedPricingModel), currencyInfo)}`}
                  subtitle={plan.description}
                  title={plan.copy}
                />
              );
            })}
        </div>
      </div>
      {isCustom && (
        <ContextualDataVariables
          contextualDataProviders={campaign.contextualDataProviders}
          contextualThirdPartyProviderVariables={contextualThirdPartyProviderVariables}
          currencyInfo={currencyInfo}
          onContextualDataProviderChange={handleContextualDataProviderChange}
          user={user}
          userRoles={userRoles}
        />
      )}
      {selectedUserPlan && (
        <div className="feesBySolutionStepTwo__capabilitiesSection">
          <span className="feesBySolutionStepTwo__capabilitiesSectionTitle">
            {formatMessage({
              defaultMessage: 'These features are included in your selected planning type:',
              id: 'feesBySolution.stepTwo.featuresIncludedTitle'
            })}
          </span>
          {currentCapabilitiesCategoriesIds.map(id => (
            <div
              className="feesBySolutionStepTwo__capabilitiesSectionCategory"
              key={`feesBySolutionStepTwo-capabilitiesSection-category-${id}`}
            >
              <span className="feesBySolutionStepTwo__capabilitiesSectionCategoryTitle">
                {capabilityCategories?.find((category: Record<string, any>) => category._id === id)!.copy}
              </span>
              <div className="feesBySolutionStepTwo__capabilitiesSectionCategoryCapability">
                {currentCapabilities!
                  .filter((c: Record<string, any>) => c.category === id)
                  ?.map((c: Record<string, any>) => (
                    <Tag
                      id={`feesBySolutionStepTwo-${id}-${c._id}`}
                      key={`feesBySolutionStepTwo-capabilitiesSection-capability-${id}-${c._id}`}
                      status="category"
                    >
                      {c.copy}
                    </Tag>
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
